import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useNavigate, Navigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Screen, ScreenTitle, ErrorNote, PrimaryButton } from '../ui/Screen.jsx'
import { ItemStack } from '../items/index.js'
import { isAnswered } from '../items/kinds.js'
import { useConfigBundle, formatsOf, screensOf } from '../config.js'
import { beeps as beepsApi } from '../api.js'
import { saveAnswer, STALE, isBeepExpired } from '../answers.js'
import SafetyScreen from './SafetyScreen.jsx'
import { resolveCrisis, mergeOutcome, acknowledgeSafety } from '../safety.js'
import { NavBack, NavLeave, NavWordmark } from '../ui/NavBar.jsx'
import { waitingLine, waitingText, isLastOfDay } from '../waiting.js'

/**
 * One beep. Opened from a notification tap or from the app.
 *
 * EMA items never grid: each carries its own prompt and its own scale, so this
 * is ItemStack, one at a time. Answers save per item exactly as on day 0, which
 * is why a beep that expires mid-answer still keeps what was answered.
 *
 * Expiry is shown honestly when it is close. A beep expires 60 minutes after
 * firing and is never re-sent, so quietly letting it lapse while someone is
 * mid-answer would be the wrong kind of calm.
 */
const WARN_WITHIN_MS = 10 * 60 * 1000

export default function Beep() {
  const { beepId } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: bundle } = useConfigBundle()

  const beep = useQuery({
    queryKey: ['beep', beepId],
    queryFn: () => beepsApi.get(beepId),
    staleTime: 0,
  })

  const opened = useRef(false)
  const [local, setLocal] = useState({})
  const [saveError, setSaveError] = useState(null)
  const [safety, setSafety] = useState(null)
  const [now, setNow] = useState(() => Date.now())
  /*
   * WHAT THE SERVER SAID WHEN IT CLOSED THE BEEP, not the fact that we asked it
   * to. The final screen is rendered from this and never from local state
   * (DS-4 §8): `answered_count` is counted from answer ROWS, so it is the only
   * thing in the system that knows what actually landed.
   */
  const [receipt, setReceipt] = useState(null)
  /*
   * THE INSTANT THIS CHECK-IN CLOSED, FROZEN. AD-11 §2.4 rule 4: the waiting
   * estimate is computed once and never refines. `now` below ticks every 30s
   * for the expiry warning on an OPEN beep, and reading it on the closing
   * screen would turn the estimate into a live countdown — which is an
   * appointment, which is the whole thing the ruling forbids.
   */
  const [closedAt, setClosedAt] = useState(null)
  // item_id -> the error from its last failed save. Cleared per item on success.
  const [failures, setFailures] = useState({})
  // ItemStack reports its action; the band draws it (AD-3 §3).
  const [action, setAction] = useState(null)
  // The server refused something as past expiry. Authoritative over our clock.
  const [lapsed, setLapsed] = useState(false)

  // Mark opened once, on arrival. The scheduler uses opened_at to decide
  // whether the 10-minute reminder is still owed.
  useEffect(() => {
    if (opened.current || !beep.isSuccess) return
    opened.current = true
    beepsApi.open(beepId).catch(() => {
      // A failed "opened" stamp must not stop someone answering.
    })
  }, [beep.isSuccess, beepId])

  // Tick only while an expiry is worth showing.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000)
    return () => clearInterval(id)
  }, [])

  const formats = useMemo(() => formatsOf(bundle), [bundle])
  const items = beep.data?.items ?? []
  const expiresAt = beep.data?.expires_at ? Date.parse(beep.data.expires_at) : null
  const msLeft = expiresAt ? expiresAt - now : null
  // The server's verdict wins over the device clock, which may be wrong.
  const expired = lapsed || (msLeft !== null && msLeft <= 0)

  /*
   * THE SAME ENDPOINT HOME READS, ON THE TWO TERMINAL SCREENS.
   *
   * AD-11 §2.2: "the beep-completion screen and home must never disagree about
   * when the next one is, which they can today." Reading the same fact through
   * the same selector is the structural version of that; a second source here
   * is exactly how the two drift.
   *
   * Two things are wanted and neither is in this beep's own payload:
   *   - `next_beep_at`, for the waiting line on a non-last check-in
   *   - `study_complete` on the EXPIRED path, where there is no submit receipt
   *     to carry it. AD-11 §2.3: an expired beep that was the last of the study
   *     must resolve to the ending, or "The next one will arrive as usual"
   *     becomes the same false promise in a third place.
   *
   * DECLARED WITH EVERY OTHER HOOK, above every conditional return (#127): both
   * of these branches flip at runtime, and a hook behind a flipping branch is
   * the crash class that decision exists to stop.
   */
  const current = useQuery({
    queryKey: ['beeps', 'current'],
    queryFn: beepsApi.current,
    enabled: Boolean(receipt) || expired,
    staleTime: 0,
  })

  // A beep payload reports `answered` per item but not the value, so there is
  // nothing to rehydrate: what is on screen is what was answered this session.
  const answers = local

  async function record(item, answer) {
    setLocal((prev) => ({ ...prev, [item.item_id]: answer }))
    setSaveError(null)
    // Optimistic on screen, honest underneath: clear this item's old failure
    // now, and put it back below if this attempt fails too.
    setFailures((prev) => {
      if (!(item.item_id in prev)) return prev
      const next = { ...prev }
      delete next[item.item_id]
      return next
    })
    try {
      const result = await saveAnswer({ item, answer, beepId })

      if (result.status === STALE) {
        setLocal((prev) => {
          const next = { ...prev }
          delete next[item.item_id]
          return next
        })
        setSaveError(
          new Error('This question was updated. Please take a look and answer it again.'),
        )
        return
      }

      // The server decides whether a rule matched and at what level. This client
      // holds no thresholds and computes no score. It also does not pick the
      // country: /api/safety/resources defaults to the participant's own
      // demo_country and falls back to the default block when there is no entry.
      // Same call as day 0. ONE implementation between the only two places a
      // participant can disclose — they had two, and one of them was missing.
      if (result.safety?.level) {
        const content = await resolveCrisis(result.safety.level, bundle)
        if (content) {
          const outcome = {
            ...content,
            rank: Number(result.safety.level) || 0,
            dismissalRequired: result.safety.dismissal_required ?? content.dismissalRequired,
            /*
             * THE ONLY HANDLE ON THE EVENTS THIS ANSWER RAISED (#132). Carried
             * from here to the dismissal, because that is the one moment we can
             * say she was SHOWN the resources rather than that a rule fired.
             * One answer can raise three rules and shows one screen; the server
             * stamps all three from this one id.
             */
            answerIds: result.safety.answer_id ? [result.safety.answer_id] : [],
          }
          // Merged rather than replaced: a second outcome can land while the
          // first is still on screen, and its answer must still be acknowledged
          // even when its (lower) screen is the one suppressed.
          setSafety((prev) => mergeOutcome(prev, outcome))
        }
      }
    } catch (err) {
      // The beep lapsed while they were answering. The server wrote nothing and
      // will not accept a late row, so say so rather than showing a save error
      // they can do nothing about.
      if (isBeepExpired(err)) return setLapsed(true)
      /*
       * AGAINST THE ITEM, NOT AGAINST THE SCREEN. This used to set a single
       * screen-level error under the whole stack, which on a one-item-per-page
       * beep meant it scrolled out of the way and, worse, said nothing about
       * WHICH answer was lost. The screen-level slot stays for failures that
       * genuinely belong to the whole check-in, like submit.
       */
      setFailures((prev) => ({ ...prev, [item.item_id]: err }))
      return false
    }
    return true
  }

  async function submit() {
    /*
     * NEVER CLOSE A CHECK-IN WE ALREADY KNOW DID NOT LAND.
     *
     * A beep cannot be reopened, so submitting over known failures is the exact
     * act that made ten rejected answers look like a completed check-in. Retry
     * what failed first; if anything still fails, stay here — the inline
     * messages against those items now say so, and Done remains pressable.
     */
    const outstanding = Object.keys(failures)
    if (outstanding.length) {
      const results = await Promise.all(
        outstanding.map((id) => {
          const item = items.find((i) => i.item_id === id)
          return item ? record(item, local[id]) : Promise.resolve(true)
        }),
      )
      if (results.some((ok) => ok === false)) return
    }

    try {
      const result = await beepsApi.submit(beepId)
      // The receipt, not a local flag. Everything the closing screen says is
      // read back off this.
      setReceipt(result ?? {})
      setClosedAt(Date.now())
      /*
       * SUPPORT AFTER, NOT DURING (the CEO, 2026-09-11). Purging and fainting
       * are recorded silently when answered and the submit receipt says so;
       * the help-numbers screen (the level-1 copy) shows ONCE here, before the
       * closing screen, through the same SafetyScreen branch and the same
       * acknowledgement a mid-check-in trigger uses. Nothing new to render.
       */
      if (result?.support_after?.level) {
        const content = await resolveCrisis(result.support_after.level, bundle)
        if (content) {
          setSafety({
            ...content,
            rank: Number(result.support_after.level) || 1,
            dismissalRequired: false,
            answerIds: result.support_after.answer_ids ?? [],
          })
        }
      }
      /*
       * Home's copy of `beeps/current` is now stale in the one way that
       * matters: this submit may have been the thirtieth, and the study may
       * have just ended. Marking it stale is what makes home refetch on mount
       * instead of rendering a cached `study_complete: false` and putting the
       * counter in front of someone who has finished.
       */
      queryClient.invalidateQueries({ queryKey: ['beeps', 'current'] })
    } catch (err) {
      if (isBeepExpired(err)) return setLapsed(true)
      setSaveError(err)
    }
  }

  // A crisis screen takes the whole screen and, at level 3, must be dismissed
  // explicitly before anything else can happen.
  if (safety) {
    return (
      <SafetyScreen
        level={safety.level}
        resources={safety.resources}
        countryName={safety.countryName}
        // DS-12 §1.1: this is the path that actually reaches level 3 (SF01 >= 3,
        // SF06 == 1), so it is where the displaced control earns its keep.
        // Levels 1-2 keep the normal action position: "hardly ever" must not
        // cost what the most serious answer in the instrument costs.
        dismissalRequired={safety.dismissalRequired === true}
        /*
         * SAY SHE SAW IT (#132). Fire and forget: acknowledgeSafety swallows
         * its own failure, so the dismissal is never gated, never retried and
         * never re-shown. #119 stands — this adds no step and changes no label.
         */
        onDismiss={() => {
          acknowledgeSafety(safety.answerIds)
          setSafety(null)
        }}
      />
    )
  }

  if (beep.isLoading || !bundle) {
    return (
      <Screen support nav={{ left: <NavLeave to="/" /> }}>
        <ScreenTitle>Loading your check-in</ScreenTitle>
        <p className="text-sm text-[var(--ink-700)]">One moment…</p>
      </Screen>
    )
  }

  if (beep.isError) {
    // A beep that does not resolve — a bad or foreign id — used to print the
    // server's own words, "no such beep", onto a participant's screen. Same
    // class as the pydantic email error and the retry that could never work:
    // internal text is written for whoever reads the logs, not for the person
    // holding the phone. The heading already says what happened, so this says
    // what it means for them and what they can do instead.
    const unresolvable = beep.error?.status === 404 || beep.error?.status === 422
    return (
      <Screen support nav={{ left: <NavLeave to="/" /> }}>
        <ScreenTitle>We couldn&rsquo;t open this check-in</ScreenTitle>
        {unresolvable ? (
          <p className="text-base leading-relaxed text-[var(--ink-700)]">
            This link doesn&rsquo;t point to one of your check-ins. It may be from an older
            reminder. Your next one will arrive as usual, and nothing has been lost.
          </p>
        ) : (
          <ErrorNote error={beep.error} />
        )}
      </Screen>
    )
  }

  /*
   * THE CLOSING SCREEN IS EARNED, NOT REACHED (DS-4 §8).
   *
   * It used to render on a local `submitted` flag, which meant it said "That's
   * this one done" whenever the client ran out of items — including the run
   * where all ten answers 500d and the beep closed carrying none.
   *
   * `answered_count` is counted from answer ROWS on the server, so it is the
   * only value in the system that knows what actually landed. If it is short of
   * what she gave us, this screen says so instead of thanking her.
   *
   * No count is shown either way. What she answered is not a score to report
   * back, and "8 of 11" invites her to read a grade off a delivery fact.
   */
  /*
   * ═══════════════════════════════════════════════════════════════════════
   * THE SELECTOR ORDER, AND IT IS THE RULE (AD-11 §2.2).
   *
   *   1. study complete   ->  the ending
   *   2. last of the day  ->  "That's it for today"
   *   3. otherwise        ->  "That's this one done" + home's waiting line
   *
   * GETTING 1 AND 2 THE WRONG WAY ROUND REINTRODUCES THE FALSE PROMISE IN A
   * NEW PLACE. On day 5 the bed-time beep is BOTH the last of its day and the
   * last of the study, so testing last-of-day first tells a woman who has just
   * finished thirty check-ins "the next one comes tomorrow".
   *
   * The payload's own shape enforces the order (backend #133): completion is a
   * TOP-LEVEL fact and `slot` is nested inside the beep it belongs to, so the
   * outer question is answered before the inner one can be asked.
   * ═══════════════════════════════════════════════════════════════════════
   */
  const waitingCopy = screensOf(bundle)?.waiting ?? null
  const studyOver = receipt?.study_complete === true || current.data?.study_complete === true

  /*
   * The ending is HOME's screen, and it renders there rather than being
   * duplicated here — §2.1 gives it no forward action at all, so a copy on this
   * route would be a screen she could reach and not leave. `invalidateQueries`
   * above has already marked home's data stale, so it refetches on mount.
   */
  if (studyOver) return <Navigate to="/" replace />

  if (receipt) {
    const attempted = Object.values(local).filter(isAnswered).length
    const landed = typeof receipt.answered_count === 'number' ? receipt.answered_count : null
    const shortfall = landed !== null && landed < attempted
    const lastOfDay = isLastOfDay(receipt.slot)

    return (
      <Screen
        support
        disposition="status"
        nav={{ left: <NavWordmark /> }}
        action={<PrimaryButton onClick={() => navigate('/')}>Done</PrimaryButton>}
      >
        <ScreenTitle>
          {shortfall
            ? 'Some answers didn’t reach us'
            : waitingText(waitingCopy, lastOfDay ? 'last_of_day_title' : 'beep_done_title')}
        </ScreenTitle>
        {shortfall ? (
          <>
            <p className="text-base leading-relaxed text-[var(--ink-900)]">
              This check-in is closed, and not everything you answered was saved. That is our
              problem, not yours, and nothing you can do now will change it.
            </p>
            {/* One way onward from every terminal screen — DS-4. Without it
                this is a dead end reached at the worst moment. */}
            <p className="mt-4 text-base leading-relaxed text-[var(--ink-700)]">
              Your next check-in will arrive as usual.
            </p>
          </>
        ) : lastOfDay ? (
          /* (b) The bed beep. It names a DAY and never a time — a day is not
             plannable at a resolution that damages sampling. No estimate. */
          <p className="text-base leading-relaxed text-[var(--ink-700)]">
            {waitingText(waitingCopy, 'last_of_day_body')}
          </p>
        ) : (
          /*
            "We'll let you know when the next one is ready." IS DELETED. It was
            the same promise home used to make, in the same null state, and on
            the last check-in of the study it was the same lie. It is replaced
            by the line home shows — literally the same function on the same
            endpoint — so the two surfaces cannot disagree about when the next
            one is, which they could until now.

            `closedAt`, not `now`: frozen at submit, so the estimate does not
            refine under the 30-second tick while she reads it.

            `isFetchedAfterMount` for the same reason as the expired screen
            below: this shares home's cache key, so `data` is populated the
            instant the screen appears — from a payload fetched before she
            started answering. An estimate computed off that is a guess wearing
            the server's authority. Until this mount has heard back,
            `next_beep_at` is null here and the lead line stands alone.
          */
          <p className="text-base leading-relaxed text-[var(--ink-700)]">
            {waitingLine(
              waitingCopy,
              current.isFetchedAfterMount ? (current.data?.next_beep_at ?? null) : null,
              closedAt ?? Date.now(),
            )}
          </p>
        )}
      </Screen>
    )
  }

  if (expired) {
    /*
     * (c) THE EXPIRED CHECK-IN — AD-11 §2.3, CFG-41 §C.
     *
     * THE ONE SURFACE IN THE DAY-TO-DAY UI THAT STATES THE 30-MINUTE RULE, and
     * the licence is narrow: the app may explain a rule that has already bitten,
     * and may never publish a rule she could act on. Here the beep is already
     * gone, so the fact cannot be planned around, and this is the only moment
     * where the rule explains something that has just happened to her.
     *
     * "check-in", never "beep": beep is study jargon and appears in no
     * participant-facing string anywhere. Introducing a second word for the same
     * thing at the moment she has just lost one is the worst place in the app
     * to do it.
     *
     * HOW EACH CLAUSE STOPS IT READING AS A TELLING-OFF:
     *   - the APP apologises first, so she is not the subject of the opening
     *     sentence
     *   - the rule is a property of CHECK-INS, not of her: never "you didn't
     *     answer in time", never "you missed this one", never a count
     *   - her work is kept, said BEFORE the consequence
     *   - "Missing one is completely fine" states the cost as nil, plainly
     *
     * No --critical, no error block, no icon, no alarm. Missing a check-in is a
     * fact about a window, not something she did wrong, and red is a scolding
     * register.
     */
    return (
      <Screen support nav={{ left: <NavLeave to="/" /> }}>
        <ScreenTitle>This check-in has closed</ScreenTitle>
        <p className="text-base leading-relaxed text-[var(--ink-700)]">
          Sorry - check-ins close 60 minutes after they arrive, and this one&rsquo;s time has
          passed.
        </p>
        <p className="mt-4 text-base leading-relaxed text-[var(--ink-700)]">
          Anything you answered has been saved. Missing one is completely fine.
        </p>
        {/*
          HELD UNTIL WE KNOW, AND `isFetchedAfterMount` IS WHY.

          If this expiry was the last event of the study there is no next one,
          and "The next one will arrive as usual" becomes the same false promise
          this whole change removes. So it renders only on the server's explicit
          `study_complete: false`, and only once THIS mount has heard it.

          `['beeps', 'current']` is home's key, so `data` is populated from
          home's cache the instant this screen appears — with a
          `study_complete` that predates the expiry we are looking at. Reading
          it would put the false sentence on screen for one round trip before
          the refetch corrected it, which is the same defect wearing a shorter
          duration. Silence until the server has answered.
        */}
        {current.isFetchedAfterMount && current.data?.study_complete === false ? (
          <p className="mt-4 text-base leading-relaxed text-[var(--ink-700)]">
            The next one will arrive as usual.
          </p>
        ) : null}
      </Screen>
    )
  }

  return (
    <Screen
      support
      stop
      nav={{
        left: action?.canGoBack ? <NavBack onBack={action.goBack} /> : <NavLeave to="/" />,
        // NO PROGRESS ON A BEEP AT ALL (§4.4).
        right: null,
      }}
      notice={action?.notice ?? null}
      action={
        action ? (
          <PrimaryButton onClick={action.press} incomplete={action.incomplete}>
            {action.label}
          </PrimaryButton>
        ) : null
      }
    >
      <ScreenTitle subtitle="This is a dozen questions at most.">How are things right now?</ScreenTitle>

      {msLeft !== null && msLeft < WARN_WITHIN_MS ? (
        <p className="mb-4 rounded-[var(--r-surface)] bg-[var(--surface-sunken)] px-4 py-3 text-sm text-[var(--ink-900)]">
          This check-in closes in about {Math.max(1, Math.round(msLeft / 60000))} minutes. Whatever
          you answer is saved as you go.
        </p>
      ) : null}

      <ItemStack
        items={items}
        formats={formats}
        answers={answers}
        onAnswer={record}
        onAction={setAction}
        onComplete={submit}
        pageSize={1}
        finishLabel="Done"
        failures={failures}
        onRetry={(item) => record(item, local[item.item_id])}
      />

      <ErrorNote error={saveError} />
    </Screen>
  )
}
